import { set } from 'lodash';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import React, { useState } from 'react';
import { useHistory } from 'react-router';
import validator from 'validator';
import Button, { LoadingButton } from '@atlaskit/button';
import ButtonGroup from '@atlaskit/button/button-group';
import Form, { ErrorMessage, Field, FormApi, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import {
  BillingDetails,
  ErrorComponents,
  InfoPanel,
  IsoSelectItem,
  getRouteFromDirectMatch,
  successNotificationBuilder,
  useFormSubmit,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { CommerceAddress } from '@css-xp/graphtypes';
import { GraphEntity } from '@css-xp/graphtypes/src/generated-types-new';
import { createTransactionAccountDetails } from '@atlassian/temp-proxies';
import { UpdateTransactionAccountPayload } from '@atlassian/cssxp-types-billing';

interface FormData {
  billToName?: string | null;
  billToAddress1?: string | null;
  billToAddress2?: string | null;
  billToCity?: string | null;
  billToCountry?: IsoSelectItem | null;
  billToPostalCode?: string | null;
  billToState?: IsoSelectItem | null;
  billToTaxId?: string | null;

  shipToName?: string | null;
  shipToAddress1?: string | null;
  shipToAddress2?: string | null;
  shipToCity?: string | null;
  shipToCountry?: IsoSelectItem | null;
  shipToPostalCode?: string | null;
  shipToState?: IsoSelectItem | null;
  shipToTaxId?: string | null;

  billingAdmin?: string;
}
/**
 * This component is to dispaly sidebar to create transaction account
 * */
export const CreateTransactionAccountSidebar = () => {
  const { closeSidebar } = useSidebar();
  const history = useHistory();
  const { error: formError, isProcessing, submitForm, complete, data: response } = useFormSubmit<
    UpdateTransactionAccountPayload,
    any
  >({
    submitFormAction: createTransactionAccountDetails,
    onComplete: () => {
      closeSidebar();
    },
  });

  const [isChecked, setIsChecked] = useState(false);

  const [newShipToAddress, setShipToAddress] = useState<CommerceAddress>();

  const onSameAsBillTo = (isCheckboxEnabled: boolean, billingData: FormData) => {
    const formBillingAddress = {
      name: billingData.billToName,
      addressLine1: billingData.billToAddress1,
      addressLine2: billingData.billToAddress2,
      city: billingData.billToCity,
      state: billingData.billToState?.value,
      postcode: billingData.billToPostalCode,
      taxId: billingData.billToTaxId,
      country: billingData.billToCountry?.value,
    } as CommerceAddress;

    setIsChecked(isCheckboxEnabled);
    if (isCheckboxEnabled) {
      setShipToAddress(formBillingAddress);
    }
  };

  const submitBillingDetails = (billingData: FormData, formApi: FormApi<FormData>) => {
    // @atlaskit/form does not handle default form values well, creating thing like `selectedAddOns[0].selectedAddOn` instead of creating an object at that index. Lodash has our back.
    const transformedData: FormData = {};
    // setting key to any as getFieldState uses the actual keys of FormData, which does not include array indices
    formApi.getRegisteredFields().forEach((key: any) => {
      // get the value from the field state or the form data
      const value = formApi.getFieldState(key)?.value ?? get(billingData, key);
      set(transformedData, key, value);
    });

    const updateTransactionAccountPayload: UpdateTransactionAccountPayload = {
      billToAddress: {
        name: defaultTo(transformedData.billToName || null, ''),
        postalAddress: {
          line1: defaultTo(transformedData.billToAddress1 || null, ''),
          line2: defaultTo(transformedData.billToAddress2 || null, null),
          city: transformedData?.billToCity ?? '',
          state: defaultTo(transformedData?.billToState?.value || null, null),
          postcode: transformedData?.billToPostalCode ?? '',
          country: transformedData?.billToCountry?.value ?? '',
        },
        taxId: transformedData?.billToTaxId || null,
      },
      shipToAddress: {
        id: null,
        name: defaultTo(transformedData.shipToName || null, ''),
        postalAddress: {
          line1: defaultTo(transformedData.shipToAddress1 || null, ''),
          line2: defaultTo(transformedData.shipToAddress2 || null, null),
          city: transformedData?.shipToCity ?? '',
          state: defaultTo(transformedData?.shipToState?.value || null, null),
          postcode: transformedData?.shipToPostalCode ?? '',
          country: transformedData?.shipToCountry?.value ?? '',
        },
        // CCP has strict checks on tax ID format, and won't allow empty strings
        taxId: transformedData.shipToTaxId || null,
      },
      billingAdmin: transformedData.billingAdmin,
    };

    submitForm(updateTransactionAccountPayload);
  };
  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

  if (complete && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .withId('create-transaction-account-success')
      .withMessage('Successfully created transaction account.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    history.push(
      getRouteFromDirectMatch({
        entity: GraphEntity.TRANSACTION_ACCOUNT,
        identifier: response?.data,
      })
    );
  }
  return (
    <div className="pane-sidebar-content" style={{ maxWidth: '500px', padding: '25px' }}>
      <Form onSubmit={submitBillingDetails}>
        {({ formProps, submitting, setFieldValue, getValues }) => (
          <form {...formProps}>
            <FormHeader title="Create transaction account"></FormHeader>
            <FormSection>
              {formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to create transaction account for the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              <h3>Billing admins</h3>
              <Field
                name={`billingAdmin`}
                defaultValue={null}
                isRequired={true}
                label={'Billing admin'}
                validate={(event) => {
                  if (event) {
                    return validator.isEmail(event) ? undefined : `${event} must be a valid e-mail address`;
                  }
                }}
              >
                {({ fieldProps, error }: any) => (
                  <>
                    <Textfield
                      {...fieldProps}
                      placeholder="Atlassian Account email address"
                      autoComplete="off"
                      testId="billingAdmin"
                      isRequired={true}
                      onChange={(event: React.FormEvent<HTMLInputElement>) => {
                        fieldProps.onChange(event);
                      }}
                    />
                    {error && <ErrorMessage>{error}</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormSection>
              <BillingDetails
                billToAddress={null}
                shipToAddress={newShipToAddress}
                isEditable={true}
                setFieldValue={setFieldValue}
                //@ts-ignore -- Complaining about onSameAsBillTo
                onSameAsBillTo={(flag: boolean) => onSameAsBillTo(flag, getValues())}
                isChecked={isChecked}
              />
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={submitting || isProcessing}
                  testId={submitting || false ? 'loading-button' : 'save-button'}
                >
                  Save
                </LoadingButton>
                <Button appearance="subtle" onClick={closeSidebar}>
                  Close
                </Button>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
};
